import { UPDATE_FILTER_OPTIONS_ACTIONS } from '../../constants/campaigns';
import { Panel } from '../panel';
import { AuraSelect } from '../search';
import {
  StyledPanelHeader,
  Container,
  Row,
  FilterWrapper,
  Col,
  Text,
} from './styled';
import { Button, Icon, Intent, InputGroup } from '@blueprintjs/core';
// import { KeyValuesArray } from './index';
import { useCallback, useMemo, useState } from 'react';
import { useFilterOptionsContext } from './useFilterOptionsContext';
import DateAndComparisonDateSelectors from '../date-and-comparison-date-selector';
import * as PREVIOUS_PERIOD_PRESETS from '../../constants/previous-period-presets';
import { AuraFilterMenu } from '../filter-menu';
import { useHistory } from 'react-router-dom';
import * as ROUTES from '../../constants/routes';
import useLoadingHandler from '../../hooks/use-loading-handler';

/**
 * Campaign Manager Filter Row component
 */
const CampaignManagerFilterRow = ({
  locations,
  isLoading,
  availableMetrics,
  saveCampaign,
}) => {
  const { state, updateFilterOptions, ctxFetched } = useFilterOptionsContext();
  const {
    filterOptions,
    enabledLocations,
    taxonomyList,
    taxonomyKeyStrings,
    lineTaxonomyList,
    lineTaxonomyKeyStrings,
    defaultBreakdown,
  } = state;
  const history = useHistory(); 

  const handleFilterUpdate = useCallback(
    (type, value) => {
      updateFilterOptions(type, value);
    },
    [updateFilterOptions],
  );

  const sortedLocations = useMemo(() => {
    return locations.sort((a, b) =>
      a.name.localeCompare(b.name, 'en', { numeric: true }),
    );
  }, [locations]);

  // Get test metric options similar to headcount planner
  const testMetricOptions = useMemo(() => {
    return [
      {
        key: 'entries',
        display: 'Store > Entries',
        type: 'entrance',
        unitType: 'number',
      },
      {
        key: 'capture_rate',
        display: 'Store > Capture rate',
        type: 'entrance',
        unitType: 'percentage',
      },
      {
        key: 'average_dwell_time',
        display: 'Store > Dwell time',
        type: 'entrance',
        unitType: 'seconds',
      },
      ...(availableMetrics.sales?.metrics.filter(
        (m) => m.key === 'sales_conversion_rate',
      ).length
        ? [
          {
            key: 'sales_conversion_rate',
            display: 'Sales > Conversion rate',
            type: 'sales',
            unitType: 'percentage',
          },
        ]
        : []),
      {
        key: 'area_entries',
        display: 'Area > Entries',
        type: 'area',
        unitType: 'number',
      },
      {
        key: 'area_entries_per_location_entrant',
        display: 'Area > Entry rate',
        type: 'area',
        unitType: 'rate',
      },
      {
        key: 'area_total_dwell_time',
        display: 'Area > Total time',
        type: 'area',
        unitType: 'seconds',
      },
      {
        key: 'area_average_dwell_time',
        display: 'Area > Average time',
        type: 'area',
        unitType: 'seconds',
      },
      ...(!window.isDemoOrg &&
        availableMetrics.movementWithFilters?.metrics.filter(
          (m) => m.key === 'internal_entries',
        ).length
        ? [
          {
            key: 'internal_entries',
            display: 'Internal > Entries',
            type: 'line',
            unitType: 'number',
          },
        ]
        : []),
      ...(!window.isDemoOrg &&
        availableMetrics.movementWithFilters?.metrics.filter(
          (m) => m.key === 'internal_capture_rate',
        ).length
        ? [
          {
            key: 'internal_capture_rate',
            display: 'Internal > Entry rate',
            type: 'line',
            unitType: 'rate',
          },
        ]
        : []),
    ];
  }, []);

  // Get control locations (all locations not selected as test locations)
  const availableControlLocations = useMemo(() => {
    return sortedLocations.filter(
      (location) => !filterOptions.testLocations.includes(location.id),
    );
  }, [sortedLocations, filterOptions.testLocations]);

  const enabledControlLocations = useMemo(() => {
    const a = availableControlLocations.map(loc => loc.id).filter((id) =>
      enabledLocations.includes(id),
    );
    return a;
  }, [availableControlLocations, enabledLocations]);

  const availableTestLocations = useMemo(() => {
    return sortedLocations.filter(
      (location) => !filterOptions.controlLocations.includes(location.id),
    );
  }, [sortedLocations, filterOptions.controlLocations]);

  const [nameError, setNameError] = useState('');

  const handleNameChange = (e) => {
    const value = e.target.value;
    if (value.length < 1) {
      setNameError('Campaign name must be at least 1 character.');
    } else if (value.length > 100) {
      setNameError('Campaign name must be at most 100 characters.');
    } else {
      setNameError('');
    }
    handleFilterUpdate(UPDATE_FILTER_OPTIONS_ACTIONS.NAME, value);
  };

  return (
    <FilterWrapper>
      <Panel>
        <StyledPanelHeader>
          <Row>
            {/* Step 1: Test Metric */}
            <AuraSelect
              id="testMetric"
              key={'type'}
              activeItem={filterOptions.metric}
              items={testMetricOptions}
              textSelector={'display'}
              keySelector={'key'}
              defaultText={'Select Test Metric'}
              handleChange={(value) =>
                handleFilterUpdate(UPDATE_FILTER_OPTIONS_ACTIONS.METRIC, value)
              }
              itemsTypeText={'Metric'}
            />

            {/* Step 2: Area Filter (only if metric relates to area) */}
            {filterOptions.metric && filterOptions.metric.type === 'area' && (
              <AuraFilterMenu
                id="testArea"
                type={'hierarchical'}
                allItems={taxonomyList}
                allKeyStrings={taxonomyKeyStrings}
                selectedItems={filterOptions?.taxonomies}
                updateSelectedItems={(items) =>
                  handleFilterUpdate(
                    UPDATE_FILTER_OPTIONS_ACTIONS.TAXONOMIES,
                    items,
                  )
                }
                isLoading={!ctxFetched.current ? isLoading : false}
                noItemsText={'No areas found'}
                itemsTypeText={'Areas'}
                canBeEmpty={false}
                disableMultipleKeys={true}
              />
            )}
            {filterOptions.metric && filterOptions.metric.type === 'line' && (
              <AuraFilterMenu
                id="testArea"
                type={'hierarchical'}
                allItems={lineTaxonomyList}
                allKeyStrings={lineTaxonomyKeyStrings}
                selectedItems={filterOptions?.lineTaxonomies}
                updateSelectedItems={(items) =>
                  handleFilterUpdate(
                    UPDATE_FILTER_OPTIONS_ACTIONS.LINE_TAXONOMIES,
                    items,
                  )
                }
                isLoading={!ctxFetched.current ? isLoading : false}
                noItemsText={'No lines found'}
                itemsTypeText={'Lines'}
                canBeEmpty={false}
                disableMultipleKeys={true}
              />
            )}
            {/* Step 3: Demographic Filter */}
            {filterOptions.metric && filterOptions.metric.type !== 'sales' && (
              <AuraFilterMenu
                id="demographic"
                type={'sub-menu'}
                allItems={filterOptions.availableBreakdowns}
                selectedItems={filterOptions.selectedBreakdowns}
                updateSelectedItems={(items) =>
                  handleFilterUpdate(
                    UPDATE_FILTER_OPTIONS_ACTIONS.BREAKDOWN,
                    items,
                  )
                }
                isLoading={false}
                noItemsText={'No demographics found'}
                itemsTypeText={'demographics'}
                disableMultipleKeys={false}
                defaultTo={defaultBreakdown}
              />
            )}
          </Row>
          <Row>
            {/* Step 3: Test Location */}
            {/* {filterOptions.metric && isTaxonomySelected && ( */}
            <>
              <AuraFilterMenu
                id="testLocation"
                type={'default'}
                allItems={availableTestLocations}
                enabledItems={enabledLocations}
                selectedItems={filterOptions.testLocations}
                updateSelectedItems={(locs) =>
                  handleFilterUpdate(
                    UPDATE_FILTER_OPTIONS_ACTIONS.TEST_LOCATIONS,
                    locs,
                  )
                }
                isLoading={false}
                noItemsText={'No locations found'}
                itemsTypeText={'test locations'}
                canSelectAll={true}
                canBeEmpty={false}
              />
              {filterOptions.testLocations &&
                filterOptions.testLocations.length > 0 && (
                  <>
                    <Col>
                      <Text>compared to</Text>
                    </Col>
                    <AuraFilterMenu
                      id="controlLocation"
                      type={'default'}
                      allItems={availableControlLocations}
                      enabledItems={enabledControlLocations}
                      selectedItems={filterOptions.controlLocations}
                      updateSelectedItems={(locs) =>
                        handleFilterUpdate(
                          UPDATE_FILTER_OPTIONS_ACTIONS.CONTROL_LOCATIONS,
                          locs,
                        )
                      }
                      isLoading={false}
                      noItemsText={'No control locations available'}
                      itemsTypeText={'control locations'}
                      canSelectAll={true}
                      canBeEmpty={true}
                    />
                  </>
                )}
            </>
            {/* )} */}
          </Row>
          {filterOptions.testLocations &&
            filterOptions.testLocations.length > 0 && (
              <Row>
                {/* Step 5: Test Date and Control Date */}
                <DateAndComparisonDateSelectors
                  id="dateRanges"
                  period={filterOptions.testPeriod}
                  currentSelectedDatePreset={
                    filterOptions.testPeriod.selectedPreset
                  }
                  compareToPastPeriod={filterOptions.controlPeriod}
                  currentSelectedComparisonDatePreset={
                    filterOptions.controlPeriod.active &&
                      filterOptions.controlPeriod.selectedPreset
                      ? filterOptions.controlPeriod.selectedPreset
                      : PREVIOUS_PERIOD_PRESETS.none
                  }
                  handlePresetClick={(items) =>
                    handleFilterUpdate(
                      UPDATE_FILTER_OPTIONS_ACTIONS.TEST_PERIOD,
                      items,
                    )
                  }
                  handleComparisonPresetClick={(items) =>
                    handleFilterUpdate(
                      UPDATE_FILTER_OPTIONS_ACTIONS.CONTROL_PERIOD,
                      items,
                    )
                  }
                />
              </Row>
            )}
          {filterOptions.testPeriod && (
            <Row>
              <Col>
                <Text>Campaign name:</Text>
              </Col>
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: '300px' }}>
                {/* Step 8: Campaign Name */}
                <InputGroup
                  id="campaignName"
                  placeholder="Enter campaign name"
                  value={filterOptions.name}
                  onChange={handleNameChange}
                  style={{ minWidth: '300px' }}
                  intent={nameError ? Intent.DANGER : Intent.NONE}
                />
                {nameError && (
                  <div style={{ color: 'red', marginTop: 4 }}>{nameError}</div>
                )}
              </div>
              {/* Step 9: Save Campaign Button */}
              <Button
                intent={Intent.PRIMARY}
                text="Save Campaign"
                icon="floppy-disk"
                disabled={
                  !filterOptions.name ||
                  !('overallUpliftDelta' in state.dataUplift) ||
                  !!nameError ||
                  isLoading
                }
                onClick={() => saveCampaign(filterOptions, state.dataUplift)}
              />
              <Button
                intent={Intent.NONE}
                text="Cancel"
                icon="undo"
                onClick={() => history.push(ROUTES.CAMPAIGNS)}
              />
            </Row>
          )}
        </StyledPanelHeader>
      </Panel>
    </FilterWrapper>
  );
};

export default CampaignManagerFilterRow;
